import { useQuery } from '@tanstack/react-query';
import { Clock, TrendingUp } from 'lucide-react';
import {
  getPendingInferenceEarnings,
  type PendingInferenceEarnings
} from '@/api/fleet';

/**
 * Format a USD amount, USD-locale.
 */
const formatUsd = (usd: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(usd);

/**
 * Short relative-time formatter. "12 min ago" / "5 hr ago" / "just now".
 */
const formatRelative = (iso: string | null) => {
  if (!iso) {
    return null;
  }
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) {
    return null;
  }
  const delta = Date.now() - t;
  const min = Math.round(delta / 60_000);
  if (min < 1) {
    return 'just now';
  }
  if (min < 60) {
    return `${min} min ago`;
  }
  const hr = Math.round(min / 60);
  if (hr < 24) {
    return `${hr} hr ago`;
  }
  return `${Math.round(hr / 24)} d ago`;
};

function PanelBody({ data }: { data: PendingInferenceEarnings }) {
  const { pending, lastSettlement, sharePercent } = data;
  // Hide entirely when there's no activity AND no recent settlement to
  // anchor against. Bandwidth/mining-only operators shouldn't see an
  // empty inference panel.
  if (pending.jobs === 0 && !lastSettlement) {
    return null;
  }

  const pendingUsd = pending.operatorUsd;
  const oldestRelative = formatRelative(pending.oldestJobAt);
  const settledRelative = formatRelative(lastSettlement?.at ?? null);

  return (
    <section className="rounded-xl border border-emerald-500/25 bg-gradient-to-br from-emerald-500/5 to-emerald-500/[0.02] p-4 lg:p-5">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-500/15 text-emerald-300">
            <TrendingUp className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-nurev-text">
              Pending inference earnings
            </h2>
            <p className="mt-0.5 text-xs text-nurev-text-soft">
              {pending.jobs > 0 ? (
                <>
                  {pending.jobs} job{pending.jobs === 1 ? '' : 's'} served
                  this window
                  {oldestRelative && (
                    <>
                      {' '}
                      · oldest <span className="text-nurev-muted">{oldestRelative}</span>
                    </>
                  )}{' '}
                  · {sharePercent}% operator share applied · settles{' '}
                  <span className="text-nurev-muted">hourly with random jitter</span>
                </>
              ) : (
                <>
                  Nothing in flight right now. New jobs show up here within
                  seconds of being served.
                </>
              )}
            </p>
          </div>
        </div>
        <div className="md:text-right">
          <div className="text-3xl font-bold leading-tight text-emerald-300">
            {formatUsd(pendingUsd)}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-nurev-muted">
            will post next cycle
          </div>
        </div>
      </div>

      {lastSettlement && (
        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-emerald-500/15 pt-3 text-xs text-nurev-text-soft">
          <span className="inline-flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5 text-nurev-muted" />
            Last settled {settledRelative ?? new Date(lastSettlement.at).toLocaleString()}
          </span>
          <span className="text-nurev-muted">·</span>
          <span>
            <span className="font-semibold text-nurev-text">
              {formatUsd(lastSettlement.cents / 100)}
            </span>{' '}
            across {lastSettlement.jobs} job{lastSettlement.jobs === 1 ? '' : 's'}
          </span>
        </div>
      )}
    </section>
  );
}

export function PendingInferencePanel() {
  const { data, isLoading } = useQuery({
    queryKey: ['fleet-pending-inference'],
    queryFn: getPendingInferenceEarnings,
    refetchInterval: 15_000
  });

  if (isLoading || !data) {
    return null;
  }
  return <PanelBody data={data} />;
}
